import React, { useContext, useEffect } from 'react';
import { Container, Row, Col, Dropdown } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SettingsMenu from '../components/SettingsMenu';
import BusinessSettingsPage from './BusinessSettingsPage';
import ChangePasswordPage from './ChangePasswordPage';
import UsersPage from './UsersPage';
import ProfilePage from './ProfilePage'; // Import the new ProfilePage
import LegalSettingsPage from './LegalSettingsPage'; // Import LegalSettingsPage
import PlanManagementPage from './PlanManagementPage'; // Import PlanManagementPage
import TelegramBotPage from './TelegramBotPage';
import TablesManagementPage from './TablesManagementPage';
// ⚠ Temporal: selector de paleta mientras se elige cuál se queda. Ver src/theme/.
import AppearanceSettingsPage from './AppearanceSettingsPage';
import { useTheme } from '../theme/ThemeContext';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';

const SettingsPage = () => {
    const { section } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const canManageUsers = hasPermission(PERMISSIONS.MANAGE_USERS);
    const canViewBusinessSettings = hasPermission(PERMISSIONS.VIEW_BUSINESS_SETTINGS);
    const canManagePlan = ['owner', 'superadmin'].includes(user?.role);
    // ⚠ Temporal: si el superadmin cerró la elección, la sección desaparece.
    // Se comprueba también aquí y no solo en el menú, porque a /settings/appearance
    // se puede llegar por URL directa.
    const { canChoose: canChooseTheme } = useTheme();

    const handleSelect = (key) => {
        navigate(`/settings/${key}`);
    };

    const renderSection = () => {
        switch (section) {
            case 'business':
                return canViewBusinessSettings ? <BusinessSettingsPage /> : <ProfilePage />;
            case 'plan':
                return canManagePlan ? <PlanManagementPage /> : <ProfilePage />;
            case 'change-password':
                return <ChangePasswordPage />;
            case 'users':
                return canManageUsers ? <UsersPage /> : <ProfilePage />;
            case 'profile':
                return <ProfilePage />;
            case 'legal':
                return <LegalSettingsPage />;
            case 'appearance':
                return canChooseTheme ? <AppearanceSettingsPage /> : <ProfilePage />;
            case 'telegram':
                return canViewBusinessSettings ? <TelegramBotPage /> : <ProfilePage />;
            case 'tables':
                return canViewBusinessSettings ? <TablesManagementPage /> : <ProfilePage />;
            default:
                return <ProfilePage />;
        }
    };

    const getSectionTitle = () => {
        switch (section) {
            case 'business':
                return t('settings.business');
            case 'plan':
                return t('settings.planManagement');
            case 'change-password':
                return t('settings.changePassword');
            case 'users':
                return t('settings.userManagement');
            case 'profile':
                return t('settings.profile');
            case 'legal':
                return t('settings.legal');
            case 'appearance':
                return t('settings.appearance');
            case 'telegram':
                return 'Bot de Telegram';
            case 'tables':
                return t('settings.tables');
            default:
                return t('settings.profile');
        }
    }

    return (
        <Container fluid className="mt-4">
            <div className="d-md-none mb-3">
                <Dropdown onSelect={handleSelect}>
                    <Dropdown.Toggle variant="primary" id="dropdown-basic" className="w-100">
                        {getSectionTitle()}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="w-100">
                        {canViewBusinessSettings && <Dropdown.Item eventKey="business">{t('settings.business')}</Dropdown.Item>}
                        {canManagePlan && <Dropdown.Item eventKey="plan">{t('settings.planManagement')}</Dropdown.Item>}
                        {canManageUsers && <Dropdown.Item eventKey="users">{t('settings.userManagement')}</Dropdown.Item>}
                        <Dropdown.Item eventKey="profile">{t('settings.profile')}</Dropdown.Item>
                        <Dropdown.Item eventKey="change-password">{t('settings.changePassword')}</Dropdown.Item>
                        {canChooseTheme && <Dropdown.Item eventKey="appearance">{t('settings.appearance')}</Dropdown.Item>}
                        {canViewBusinessSettings && <Dropdown.Item eventKey="telegram">Bot de Telegram</Dropdown.Item>}
                        {canViewBusinessSettings && <Dropdown.Item eventKey="tables">{t('settings.tables')}</Dropdown.Item>}
                        <Dropdown.Item eventKey="legal">{t('settings.legal')}</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            <Row>
                <Col md={2} className="d-none d-md-block">
                    <SettingsMenu />
                </Col>
                <Col md={9}>
                    {renderSection()}
                </Col>
            </Row>
        </Container>
    );
};

export default SettingsPage;

